// StoreDetailScreen.js (updated)
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiAxios as axios } from "../../utils/apiClient.js";
import "./StoreDetailScreen.css";
import { url } from "../../Constant/TextConstant";
import { useAuth } from "../../utils/AuthContext";
import { FaHeart, FaRegHeart } from "react-icons/fa";
import { toast } from "react-toastify";
import { useCart } from "../../utils/cartContext";
import { encryptData } from "../../utils/encryption";
import "react-toastify/dist/ReactToastify.css";
import { Helmet } from "react-helmet";
import { useUserLocation } from "../../utils/useUserLocation.js";
import {
  saveStoreToCollection,
  removeStoreFromCollection,
  getCollectionsForStore,
  isStoreInAnyCollection,
  saveProductToCollection,
  removeProductFromCollection,
  getCollectionsForProduct,
  isProductInAnyCollection,
  getAllCollectionNames,
  createCollection,
  getSavedDataFromLocalDB,
  getSuggestedCollections,
} from "../../utils/storeLocalDB";
import { decryptData } from "../../utils/encryption";
import Header from "../Header";
import Footer from "../Footer/Footer";

const StoreDetailScreen = () => {
  const { encryptedMobile } = useParams();
  const storeMobile = decryptData(encryptedMobile);
  const navigate = useNavigate();
  const { currentUser, currentUserName } = useAuth();

  // State variables
  const [isLoading, setIsLoading] = useState(true);
  const [storeRating, setStoreRating] = useState(0);
  const [storeDetails, setStoreDetails] = useState(null);
  const [bestDealProducts, setBestDealProducts] = useState([]);
  const [storeName, setStoreName] = useState("");
  const [regularDealProducts, setRegularDealProducts] = useState([]);
  const [exclusiveDealProducts, setExclusiveDealProducts] = useState([]);
  const [under99DealProducts, setUnder99DealProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(0);
  const [isListen, setIsListen] = useState(true);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomedImageUrl, setZoomedImageUrl] = useState("");
  const [isStoreInCollection, setIsStoreInCollection] = useState(false);
  const [totalProductCount, setTotalProductCount] = useState(0);
  const [showCollectionDialog, setShowCollectionDialog] = useState(false);
  const [collections, setCollections] = useState([]);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [dialogType, setDialogType] = useState("store"); // 'store' or 'product'
  const productImageStartingPath = url.productImageStartingPath;
  const baseAPIUrl = url.baseAPIUrl;
  const { addToCart } = useCart();

   useEffect(() => {
      window.scrollTo(0, 0);
    }, []);
    
  // Replace the existing location state with the useUserLocation hook
  const {
    location: currentLocation,
    error: locationError,
    isLoading: isLocationLoading,
    updateLocation,
  } = useUserLocation();

  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(() => {
    return localStorage.getItem("selectedCategory") || "products";
  });

  // Update localStorage whenever selectedCategory changes
  useEffect(() => {
    localStorage.setItem("selectedCategory", selectedCategory);
  }, [selectedCategory]);

  // Handler for modal close with location update
  const handleModalClose = (shouldClose, newCoordinates = null) => {
    setShowModal(false);
    if (newCoordinates) {
      // Update location through the hook's update function
      updateLocation({
        coordinates: {
          latitude: newCoordinates.latitude,
          longitude: newCoordinates.longitude,
        },
        address: newCoordinates.address || "",
        details: newCoordinates.details || null,
      });
    }
  };
  // Check if store is saved in any collection
  const checkStoreInCollections = useCallback(() => {
    setIsStoreInCollection(isStoreInAnyCollection(storeMobile));
    setCollections(getCollectionsForStore(storeMobile));
  }, [storeMobile]);

  useEffect(() => {
    checkStoreInCollections();
  }, [storeMobile, checkStoreInCollections]);

  // ErrorBoundary component
  const ErrorBoundary = ({ children, fallback }) => {
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
      const errorHandler = (error) => {
        setHasError(true);
      };

      // Add global error handlers
      window.addEventListener("error", errorHandler);
      window.addEventListener("unhandledrejection", errorHandler);

      return () => {
        window.removeEventListener("error", errorHandler);
        window.removeEventListener("unhandledrejection", errorHandler);
      };
    }, []);

    if (hasError) {
      return (
        fallback || <div className="error-fallback">Something went wrong.</div>
      );
    }

    return children;
  };

  const toggleCollectionDialog = (type = "store", product = null) => {
    setDialogType(type);
    if (product) {
      setSelectedProduct(product);
    }
    setShowCollectionDialog(!showCollectionDialog);
    if (!showCollectionDialog) {
      setNewCollectionName("");
    }
  };

  const handleSaveToCollection = (collectionName) => {
    if (dialogType === "store") {
      saveStoreToCollection(
        {
          storeMobile,
          storeName,
          storeTitle: storeDetails?.StoreTitle || "",
          shopkeeperImage: storeDetails?.ShopkeeperImage || "",
          address: storeDetails?.Address || "",
          rating,
        },
        collectionName
      );
    } else if (dialogType === "product" && selectedProduct) {
      // Save complete product data with all fields
      saveProductToCollection(
        {
          ID: selectedProduct.ID,
          ProductCode: selectedProduct.ProductCode,
          BrandName: selectedProduct.BrandName,
          ProductName: selectedProduct.ProductName,
          Category: selectedProduct.Category,
          Image1: selectedProduct.Image1,
          Image2: selectedProduct.Image2,
          Mrp: selectedProduct.Mrp,
          OurPrice: selectedProduct.OurPrice,
          Discount: selectedProduct.Discount,
          Size: selectedProduct.Size,
          Status: selectedProduct.Status,
          StoreMobile: selectedProduct.StoreMobile,
          StoreName: selectedProduct.StoreName,
          Latitude: selectedProduct.Latitude,
          Longitude: selectedProduct.Longitude,
          AverageRating: selectedProduct.AverageRating,
          Role: selectedProduct.Role,
        },
        collectionName
      );
    }
    checkStoreInCollections();
  };

  const handleCreateCollection = () => {
    if (!newCollectionName.trim()) return false;

    // Create the collection with the correct type
    const success = createCollection(newCollectionName, dialogType);

    if (success) {
      // Save to the newly created collection
      handleSaveToCollection(newCollectionName);
      setNewCollectionName(""); // Clear the input field
      return true;
    }

    return false;
  };

  const handleRemoveFromCollections = () => {
    if (dialogType === "store") {
      removeStoreFromCollection(storeMobile);
    } else if (dialogType === "product" && selectedProduct) {
      removeProductFromCollection(
        selectedProduct.ID,
        selectedProduct.StoreMobile
      );
    }

    if (dialogType === "store") {
      setIsStoreInCollection(false);
      setCollections([]);
    }

    setShowCollectionDialog(false);
  };

  const handleAddToCart = (product, e) => {
    // Default values for size and quantity
    const defaultSize = product.Size || "";
    const defaultQuantity = 1;

    // Prepare the complete product data for cart
    const productToAdd = {
      ID: product.ID || 0,
      ProductName: product.ProductName || "",
      ProductCode: product.ProductCode || "",
      OurPrice: product.OurPrice || 0,
      Mrp: product.Mrp || 0,
      DiscountPercentage: product.Discount || 0,
      Image1: product.Image1 || "",
      Image2: product.Image2 || "",
      StoreMobile: product.StoreMobile || "",
      StoreName: product.StoreName || "",
      Category: product.Category || "",
      Size: defaultSize,
      quantity: defaultQuantity,
      Total: (product.OurPrice || 0) * defaultQuantity,
      Latitude: product.Latitude || 0,
      Longitude: product.Longitude || 0,
      AverageRating: product.AverageRating || 0,
      stock: product.Stock || 10,
      addedAt: new Date().toISOString(),
    };

    // Save updated cart to localStorage
    addToCart(productToAdd);

    // Optional: Update cart count in header or other components
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          fetchStoreRating(),
          fetchStoreDetails(),
          fetchAllProducts(),
          fetchReviews(),
        ]);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [storeMobile]);

  const fetchStoreRating = async () => {
    try {
      const response = await axios.get(
        `${baseAPIUrl}/api/ShopkeeperLikeReviewAndCall/GetShopsRatingPercentage?StoreMobile=${storeMobile}`
      );
      const ratingValue = parseFloat((response.data / 20).toFixed(2));
      setStoreRating(ratingValue);
    } catch (error) {
    }
  };

  const fetchStoreDetails = async () => {
    try {
      const response = await axios.get(
        `${baseAPIUrl}/api/ShopKeeper/binddetails?StoreMobile=${storeMobile}`
      );
      setStoreDetails(response.data);
      setStoreName(response.data.StoreName);
    } catch (error) {
    }
  };

  const fetchProductData = async (endpoint) => {
    try {
      const response = await axios.get(
        `${endpoint}?StoreMobile=${storeMobile}`,
        {
          // timeout: 5000, // Add timeout to prevent hanging
          validateStatus: function (status) {
            return status >= 200 && status < 300; // Only resolve for 2xx status codes
          },
        }
      );
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      if (error.response?.status === 404) {
        return []; // Return empty array for 404 errors
      }
      return []; // Return empty array for other errors
    }
  };

  const fetchAllProducts = async () => {
    setIsLoading(true);
    try {
      setTotalProductCount(0);

      // Define all endpoints we want to try
      const endpoints = [
        `${baseAPIUrl}/api/ShowStoreProductToUser/ShowBestDealsToUser`,
        `${baseAPIUrl}/api/ShowStoreProductToUser/ShowRegularDealsToUser`,
        `${baseAPIUrl}/api/ShowStoreProductToUser/ShowExclusiveDealsToUser`,
        // Only include under99 endpoint if it's known to exist
        `${baseAPIUrl}/api/ShowStoreProductToUser/ShowUnder99ToUser`,
      ];

      // Fetch data from all endpoints that exist
      const productPromises = endpoints.map((endpoint) =>
        fetchProductData(endpoint)
      );

      const [bestDeals, regularDeals, exclusiveDeals, under99Deals] =
        await Promise.all(productPromises);


      setBestDealProducts(bestDeals);
      setRegularDealProducts(regularDeals);
      setExclusiveDealProducts(exclusiveDeals);
      setUnder99DealProducts(under99Deals); // Comment out if endpoint doesn't exist

      const total =
        bestDeals.length +
        regularDeals.length +
        exclusiveDeals.length +
        under99Deals.length; // Remove if endpoint doesn't exist
      setTotalProductCount(total);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(
        `${baseAPIUrl}/api/ShopkeeperLikeReviewAndCall/GetReviewByMobile?StoreMobile=${storeMobile}`
      );
      const reviews = response.data || [];
      setReviews(reviews);

      if (reviews.length > 0) {
        const totalRating = reviews.reduce(
          (sum, review) => sum + (review.rating || 0),
          0
        );
        setRating(totalRating / reviews.length);
      } else {
        setRating(0);
      }
    } catch (error) {
      setReviews([]);
      setRating(0);
    }
  };

  const makePhoneCall = async () => {
    try {
      await logCallData();
      window.location.href = `tel:${storeDetails.StoreMobile}`;
    } catch (error) {
    }
  };

  const logCallData = async () => {
    try {
      const storeNameEncoded = encodeURIComponent(storeName);
      await axios.post(
        `${baseAPIUrl}/api/ShopkeeperLikeReviewAndCall/CallDetails?Name=${currentUserName}&StoreName=${storeNameEncoded}&Email=&StoreMobile=${storeMobile}&CustomerMobile=${currentUser}`
      );
    } catch (error) {
    }
  };

  const openLocation = () => {
    if (storeDetails?.Latitude && storeDetails?.Longitude) {
      const lat = storeDetails.Latitude;
      const lng = storeDetails.Longitude;
      const label = encodeURIComponent(
        storeDetails.StoreName || "Store Location"
      );
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}&query_place_id=${label}`;
      window.open(googleMapsUrl, "_blank");
    } else {
      alert("Location information is not available.");
    }
  };

  const shareStore = async () => {
    try {
      const deepLinkUrl = `https://nearmeind.com/consumer?userName=${encodeURIComponent(
        storeName
      )}&storeMobile=${storeMobile}`;
      const shareMessage = `${storeDetails.toreTitle}\n\nOpen in app: ${deepLinkUrl}`;

      if (navigator.share) {
        await navigator.share({
          title: storeName,
          text: shareMessage,
        });
      } else {
        await navigator.clipboard.writeText(shareMessage);
        alert("Store link copied to clipboard!");
      }
    } catch (error) {
    }
  };

  const handleProductClick = (product) => {
    const slug = createProductSlug(product.ProductName);
    navigate(`/product/${product.ID}/${slug}`, {
      state: { product },
    });
  };

  const createProductSlug = (productName) => {
    return (
      productName
        .toLowerCase()
        .trim()
        // First replace % with '-percent-' to make it readable
        .replace(/%/g, "-percent-")
        // Then replace + with '-plus-'
        .replace(/\+/g, "-plus-")
        // Remove other special characters except hyphens
        .replace(/[^\w\s-]/g, "")
        // Replace spaces with hyphens
        .replace(/\s+/g, "-")
        // Collapse multiple hyphens
        .replace(/-+/g, "-")
        // Limit length
        .slice(0, 60)
        // Remove trailing hyphen
        .replace(/-$/, "")
    );
  };

  const renderCollectionDialog = () => {
    if (!showCollectionDialog) return null;

    const isStoreDialog = dialogType === "store";
    const currentCollections = isStoreDialog
      ? getCollectionsForStore(storeMobile)
      : selectedProduct
      ? getCollectionsForProduct(
          selectedProduct.ID,
          selectedProduct.StoreMobile
        )
      : [];

    const hasCollections = currentCollections.length > 0;
    const allCollections = getAllCollectionNames();
    const savedData = getSavedDataFromLocalDB();

    return (
      <div
        className="save-dialog-overlay"
        onClick={() => setShowCollectionDialog(false)}
      >
        <div
          className="save-dialog-content"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="save-dialog-header">
            <h3>
              {hasCollections
                ? `Manage ${isStoreDialog ? "Store" : "Product"} Collections`
                : `Save ${isStoreDialog ? "Store" : "Product"} to Collection`}
            </h3>
            <button
              className="save-dialog-close"
              onClick={() => setShowCollectionDialog(false)}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          {hasCollections ? (
            <>
              <div className="collection-list">
                <h4>This {isStoreDialog ? "store" : "product"} is saved in:</h4>
                {currentCollections.map((name) => (
                  <div key={name} className="collection-item">
                    {name}
                    <button
                      className="remove-collection-btn"
                      onClick={() => {
                        if (isStoreDialog) {
                          removeStoreFromCollection(storeMobile, name);
                        } else if (selectedProduct) {
                          removeProductFromCollection(selectedProduct.ID, name);
                        }
                        checkStoreInCollections();
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
              <button
                className="remove-all-btn"
                onClick={handleRemoveFromCollections}
              >
                Remove from Collections
              </button>
            </>
          ) : (
            <>
              <div className="collection-list">
                {allCollections.map((name) => (
                  <div
                    key={name}
                    className="collection-item"
                    onClick={() => handleSaveToCollection(name)}
                  >
                    {name}
                    <span className="product-count">
                      {isStoreDialog
                        ? savedData.storeCollections[name]?.stores.length > 99
                          ? "99+"
                          : savedData.storeCollections[name]?.stores.length || 0
                        : savedData.productCollections[name]?.products.length >
                          99
                        ? "99+"
                        : savedData.productCollections[name]?.products.length ||
                          0}
                    </span>
                  </div>
                ))}
              </div>

              <div className="new-collection-form">
                <input
                  type="text"
                  value={newCollectionName}
                  onChange={(e) => setNewCollectionName(e.target.value)}
                  placeholder="New collection name"
                  maxLength={20}
                />
                <button
                  onClick={handleCreateCollection}
                  disabled={
                    !newCollectionName.trim() || allCollections.length >= 10
                  }
                >
                  Create & Save
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderStoreHeader = () => {
    if (!storeDetails) return null;

    return (
      <div className="store-detail-header">
        <div className="store-detail-image-container">
          <img
            src={
              storeDetails.ShopkeeperImage
                ? `${productImageStartingPath}/${storeDetails.ShopkeeperImage}`
                : "/assets/images/default-store.png"
            }
            alt={storeDetails.StoreName}
            className="store-detail-image"
            onClick={() => {
              setZoomedImageUrl(
                `${productImageStartingPath}/${storeDetails.ShopkeeperImage}`
              );
              setIsZoomed(true);
            }}
          />
          <div className="store-detail-badge">
            <span className="store-detail-rating-badge">
              ⭐ {rating.toFixed(1)} ({reviews.length})
            </span>
          </div>
        </div>

        <div className="store-detail-info">
          <h1 className="store-detail-name">{storeDetails.StoreName}</h1>
          <p className="store-detail-title">{storeDetails.StoreTitle}</p>

          <div className="store-detail-meta">
            <span className="store-detail-category">
              {storeDetails.Category}
            </span>
            <span className="store-detail-distance">2.5 km away</span>
          </div>

          <div className="store-detail-actions">
            <button
              className="store-detail-action-btn store-detail-call-btn"
              onClick={makePhoneCall}
            >
              <i className="fas fa-phone"></i> Call
            </button>
            <button
              className="store-detail-action-btn store-detail-direction-btn"
              onClick={openLocation}
            >
              <i className="fas fa-directions"></i> Direction
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAddress = () => {
    if (!storeDetails?.Address) return null;

    return (
      <div className="store-detail-address-section">
        <div className="store-detail-section-header">
          <h3>Store Information</h3>
          <button className="store-detail-share-btn" onClick={shareStore}>
            <i className="fas fa-share-alt"></i>
          </button>
        </div>

        <div className="store-detail-address-card">
          <div className="store-detail-address-icon">
            <i className="fas fa-map-marker-alt"></i>
          </div>
          <div className="store-detail-address-content">
            <p>{storeDetails.Address}</p>
            <button
              className="store-detail-copy-btn"
              onClick={() => {
                navigator.clipboard.writeText(storeDetails.Address);
                alert("Address copied to clipboard!");
              }}
            >
              Copy Address
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderStoreStats = () => {
    if (!storeDetails) return null;

    return (
      <div className="store-detail-stats-section">
        <div className="store-detail-stats-grid">
          <div className="store-detail-stat-item">
            <div className="store-detail-stat-value">{totalProductCount}+</div>
            <div className="store-detail-stat-label">Products</div>
          </div>
          <div className="store-detail-stat-item">
            <div className="store-detail-stat-value">{reviews.length}</div>
            <div className="store-detail-stat-label">Reviews</div>
          </div>
          <div className="store-detail-stat-item">
            <div className="store-detail-stat-value">{storeRating}%</div>
            <div className="store-detail-stat-label">Positive</div>
          </div>
        </div>

        <div className="store-detail-action-buttons">
          <button
            className={`store-detail-favorite-btn ${
              isStoreInCollection ? "store-detail-active" : ""
            }`}
            onClick={() => toggleCollectionDialog("store")}
          >
            <i
              className={`fas ${
                isStoreInCollection ? "fa-heart" : "fa-heart-o"
              }`}
            ></i>
            {isStoreInCollection ? (
              <>Saved {collections.length > 0 && `(${collections.length})`}</>
            ) : (
              "Save"
            )}
          </button>
          <button
            className="store-detail-bulk-order-btn"
            onClick={() => navigate(`/bulk-order/${encryptData(storeMobile)}`)}
          >
            <i className="fas fa-clipboard-list"></i> List Order
          </button>
        </div>
      </div>
    );
  };

  const renderProductCategory = (products, title, subtitle) => {
    if (!products || products.length === 0) return null;

    return (
      <div className="store-detail-product-category">
        <div className="store-detail-section-header">
          <div>
            <h2>{title}</h2>
            <p className="store-detail-section-subtitle">{subtitle}</p>
          </div>
        </div>

        <div className="store-detail-product-scroll">
          {products.map((product) => {
            const isSaved = isProductInAnyCollection(
              product.ID,
              product.StoreMobile
            );
            {
            }

            return (
              <div key={product.id} className="store-detail-product-card">
                <div className="store-detail-product-image-container">
                  <img
                    src={`${productImageStartingPath}/${product.Image1}`}
                    alt={product.ProductName}
                    className="store-detail-product-image"
                    onClick={() => handleProductClick(product)}
                  />
                  {product.Discount && product.Discount !== "0" && (
                    <span className="store-detail-discount-badge">
                      {product.Discount}% OFF
                    </span>
                  )}
                  <button
                    className="store-detail-favorite-product-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCollectionDialog("product", product);
                    }}
                  >
                    {isSaved ? (
                      <FaHeart color="#e74c3c" />
                    ) : (
                      <FaRegHeart color="#95a5a6" />
                    )}
                  </button>
                </div>

                <div className="store-detail-product-info">
                  <h3
                    className="store-detail-product-name"
                    onClick={() => handleProductClick(product.id)}
                  >
                    {product.BrandName} {product.ProductName}
                  </h3>
                  <div className="store-detail-product-rating">
                    <span className="store-detail-rating-stars">
                      {[...Array(5)].map((_, i) => (
                        <i
                          key={i}
                          className={`fas fa-star ${
                            i < (product.AverageRating || 0)
                              ? "store-detail-filled"
                              : ""
                          }`}
                        ></i>
                      ))}
                    </span>
                  </div>
                  <div className="store-detail-product-prices">
                    <span className="store-detail-current-price">
                      ₹{product.OurPrice}
                    </span>
                    <span className="store-detail-original-price">
                      ₹{product.Mrp}
                    </span>
                    {product.Discount && product.Discount !== "0" && (
                      <span className="store-detail-discount-text">
                        {product.Discount}% off
                      </span>
                    )}
                  </div>
                  <button
                    className="store-detail-add-to-cart-btn"
                    onClick={(e) => {
                      if (product.StoreMobile === currentUser) {
                        const userConfirmed = window.confirm(
                          "This is your own product. You can't add it to the cart."
                        );
                        // You could do something with userConfirmed if needed.
                        return;
                      }
                      e.stopPropagation();
                      handleAddToCart(product);
                    }}
                  >
                    Add to Cart
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderReviews = () => {
    if (reviews.length === 0) return null;

    return (
      <div className="store-detail-reviews-section">
        <div className="store-detail-section-header">
          <h2>Customer Reviews</h2>
          <button
            className="store-detail-add-review-btn"
            onClick={() => navigate(`/rate-store/${storeMobile}`)}
          >
            Add Review
          </button>
        </div>

        <div className="store-detail-rating-summary">
          <div className="store-detail-average-rating">
            <div className="store-detail-rating-value">{rating.toFixed(1)}</div>
            <div className="store-detail-rating-stars">
              {[...Array(5)].map((_, i) => (
                <i
                  key={i}
                  className={`fas fa-star ${
                    i < rating ? "store-detail-filled" : ""
                  }`}
                ></i>
              ))}
            </div>
            <div className="store-detail-rating-count">
              {reviews.length} reviews
            </div>
          </div>
          <div className="store-detail-rating-bars">
            {[5, 4, 3, 2, 1].map((star) => {
              const count = reviews.filter(
                (r) => Math.round(r.rating) === star
              ).length;
              const percentage = (count / reviews.length) * 100;

              return (
                <div key={star} className="store-detail-rating-bar-item">
                  <span className="store-detail-star-label">{star} star</span>
                  <div className="store-detail-bar-container">
                    <div
                      className="store-detail-bar-fill"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <span className="store-detail-count-label">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="store-detail-review-list">
          {reviews.slice(0, 3).map((review, index) => (
            <div key={index} className="store-detail-review-card">
              <div className="store-detail-review-header">
                <div className="store-detail-reviewer-info">
                  <div className="store-detail-reviewer-avatar">
                    {review.userName ? review.userName.charAt(0) : "A"}
                  </div>
                  <div>
                    <div className="store-detail-reviewer-name">
                      {review.Name || "Anonymous"}
                    </div>
                    <div className="store-detail-review-date">
                      {review.date}
                    </div>
                  </div>
                </div>
                <div className="store-detail-review-rating">
                  {[...Array(5)].map((_, i) => (
                    <i
                      key={i}
                      className={`fas fa-star ${
                        i < (review.rating || 0) ? "store-detail-filled" : ""
                      }`}
                    ></i>
                  ))}
                </div>
              </div>
              <div className="store-detail-review-content">
                {review.reviews || "No review text provided."}
              </div>

              {(review.Image1 || review.Image2) && (
                <div className="store-detail-review-images">
                  {review.Image1 && (
                    <img
                      src={`${productImageStartingPath}/${review.Image1}`}
                      alt="Review"
                      className="store-detail-review-image"
                      onClick={() => {
                        setZoomedImageUrl(
                          `${productImageStartingPath}/${review.Image1}`
                        );
                        setIsZoomed(true);
                      }}
                    />
                  )}
                  {review.Image2 && (
                    <img
                      src={`${productImageStartingPath}/${review.Image2}`}
                      alt="Review"
                      className="store-detail-review-image"
                      onClick={() => {
                        setZoomedImageUrl(
                          `${productImageStartingPath}/${review.Image2}`
                        );
                        setIsZoomed(true);
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {reviews.length > 3 && (
          <button className="store-detail-view-all-reviews-btn">
            View All Reviews ({reviews.length})
          </button>
        )}
      </div>
    );
  };

  const renderZoomOverlay = () => {
    if (!isZoomed) return null;

    return (
      <div
        className="store-detail-zoom-overlay"
        onClick={() => setIsZoomed(false)}
      >
        <div className="store-detail-zoom-content">
          <img
            src={zoomedImageUrl}
            alt="Zoomed"
            className="store-detail-zoomed-image"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="store-detail-close-zoom"
            onClick={() => setIsZoomed(false)}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <>
        {" "}
        {isLoading && (
          <Helmet>
            <title>Loading Store Details... | NearMeInd</title>
            <meta name="description" content="Loading store details..." />
          </Helmet>
        )}
        <div className="store-detail-loading-screen">
          <div className="store-detail-spinner">
            <div className="store-detail-double-bounce1"></div>
            <div className="store-detail-double-bounce2"></div>
          </div>
          <p>Loading store details...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {storeDetails
            ? `${storeDetails.StoreName} - ${
                storeDetails.StoreTitle || "Store"
              } | NearMeInd`
            : "Store Details | NearMeInd"}
        </title>
        <meta
          name="description"
          content={
            storeDetails
              ? `Shop at ${storeDetails.StoreName} - ${
                  storeDetails.StoreTitle || ""
                }. Browse ${totalProductCount}+ products including best deals, regular deals, and exclusive offers.`
              : "Explore this store and discover great products and deals."
          }
        />
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content={
            storeDetails
              ? `${storeDetails.StoreName} | NearMeInd`
              : "Store Details | NearMeInd"
          }
        />
        <meta
          property="og:description"
          content={
            storeDetails
              ? `Shop at ${storeDetails.StoreName} - ${
                  storeDetails.StoreTitle || ""
                } with ${totalProductCount}+ products available.`
              : "Explore this store and discover great products and deals."
          }
        />
        <meta
          property="og:image"
          content={
            storeDetails?.ShopkeeperImage
              ? `${productImageStartingPath}/${storeDetails.ShopkeeperImage}`
              : "https://nearmeind.com/default-store-image.jpg"
          }
        />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <Header
        userLocation={currentLocation} // Use currentLocation from hook
        setShowModal={setShowModal}
        setUserLocation={updateLocation} // Use updateLocation from hook
        showModal={showModal}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
      />
      <div className="store-detail-container">
        <header className="store-detail-app-header">
          <button
            className="store-detail-back-button"
            onClick={() => navigate(-1)}
          >
            <i className="fas fa-chevron-left"></i>
          </button>
          <h1 className="store-detail-header-title">{storeName}'s Store</h1>
          <div className="store-detail-header-actions">
            <button className="store-detail-search-btn">
              <i className="fas fa-search"></i>
            </button>
            <button className="store-detail-cart-btn">
              <i className="fas fa-shopping-cart"></i>
            </button>
          </div>
        </header>

        <main className="store-detail-content">
          <div className="store-detail-headersection">
            {renderStoreHeader()}
            {renderAddress()}
            {renderStoreStats()}
          </div>

          <ErrorBoundary fallback={<div>Error loading products</div>}>
            {renderProductCategory(
              bestDealProducts,
              "Best Deal Products",
              "Quality meets affordability—shop now!"
            )}
          </ErrorBoundary>
          <ErrorBoundary fallback={<div>Error loading products</div>}>
            {renderProductCategory(
              regularDealProducts,
              "Regular Deal Products",
              "Affordable picks for daily needs."
            )}
          </ErrorBoundary>
          <ErrorBoundary fallback={<div>Error loading products</div>}>
            {renderProductCategory(
              exclusiveDealProducts,
              "Exclusive Deal Products",
              "Limited stock, available only here."
            )}
          </ErrorBoundary>
          {/* Only render under99 section if endpoint exists */}
          {under99DealProducts.length > 0 && (
            <ErrorBoundary fallback={<div>Error loading products</div>}>
              {renderProductCategory(
                under99DealProducts,
                "Under ₹99 Deals",
                "Discover hidden gems under ₹99."
              )}
            </ErrorBoundary>
          )}
          {renderReviews()}
        </main>

        {renderCollectionDialog()}
        {renderZoomOverlay()}
      </div>
      <Footer />
    </>
  );
};

export default StoreDetailScreen;
